import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Cube, Waveform } from "@phosphor-icons/react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const projects = [
  {
    icon: Cube,
    title: "MetaMix Tech",
    subtitle: "Medical VR Application",
    description: "Engineered a VR medical imaging application utilizing Unity and Node.js that processes DICOM, OBJ, and NRRD formats — enabling real-time access to patient data for over 300 healthcare professionals via Orthanc Server.",
    tech: ["Unity", "Node.js", "DICOM", "Orthanc Server", "VR"],
  },
  {
    icon: Waveform,
    title: "Real-Time AI Voice Assistant",
    subtitle: "Conversational AI Phone System",
    description: "Deployed a real-time voice system enabling natural phone conversations between users and AI agents. Bidirectional WebSocket audio streaming with speech-to-text/text-to-speech processing and telephony integration via Plivo/Twilio.",
    tech: ["Python", "FastAPI", "WebSocket", "Deepgram", "ElevenLabs", "Plivo", "Twilio"],
  },
];

export const SideProjects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} data-testid="side-projects-section" className="py-16 sm:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={{ staggerChildren: 0.1 }}
          className="mb-10"
        >
          <motion.span
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-4"
          >
            Side Projects
          </motion.span>
          <motion.h2
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="side-projects-heading"
            className="font-heading text-2xl sm:text-3xl font-bold text-[#EDEDED] tracking-tight"
          >
            Independent explorations.
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((project, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#121212] border border-white/[0.08] rounded-xl p-6 hover:border-white/[0.15] transition-all duration-300 group"
              data-testid={`side-project-${i}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center flex-shrink-0 group-hover:bg-white/[0.08] transition-colors">
                  <project.icon size={20} weight="regular" className="text-[#A1A1AA] group-hover:text-[#EDEDED] transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-[#EDEDED] tracking-tight">
                    {project.title}
                  </h3>
                  <span className="text-xs text-[#A1A1AA]/60 font-mono">{project.subtitle}</span>
                </div>
              </div>
              <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
                {project.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {project.tech.map((t) => (
                  <span key={t} className="text-[10px] font-mono px-2.5 py-0.5 rounded-full border border-white/[0.06] text-[#A1A1AA]/70">
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
